import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { useInactiveLabels } from '../hooks/useInactiveLabels';
import { MapContainer, TileLayer, Marker, Popup, useMap, Circle, Polygon, Pane } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { MapPin, Search, Calendar, Filter, X, Building2, RefreshCw, UserSearch, Stethoscope, Layers, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom marker icons
const createIcon = (color) => new L.Icon({
  iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${color}.png`,
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const icons = {
  default: createIcon('blue'),
  completed: createIcon('green'),
  pending: createIcon('orange'),
  cancelled: createIcon('red'),
  // Declination icons by discipline
  declination_PT: createIcon('red'),
  declination_OT: createIcon('blue'),
  declination_ST: createIcon('green'),
  declination_SN: createIcon('orange'),
  declination_HHA: createIcon('violet'),
  declination_default: createIcon('grey'),
};

// Coverage area colors for applicants
const coverageColors = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'
];

// Component to fit map bounds to markers
function FitBounds({ positions }) {
  const map = useMap();
  
  useEffect(() => {
    if (positions.length > 0) {
      const bounds = L.latLngBounds(positions.map(p => [p.lat, p.lng]));
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
    }
  }, [positions, map]);
  
  return null;
}

function ZoomToDeclinations({ declinations }) {
  const map = useMap();
  
  useEffect(() => {
    const handleZoom = () => {
      if (declinations.length > 0) {
        const bounds = L.latLngBounds(
          declinations.map(d => [d.latitude, d.longitude])
        );
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 10 });
      }
    };
    
    window.addEventListener('zoomToDeclinations', handleZoom);
    return () => window.removeEventListener('zoomToDeclinations', handleZoom);
  }, [declinations, map]);
  
  return null;
}

// Get previous week's date range
function getPreviousWeekRange() {
  const today = new Date();
  const dayOfWeek = today.getDay();
  
  // Find last Saturday (end of previous week)
  const lastSaturday = new Date(today);
  lastSaturday.setDate(today.getDate() - dayOfWeek - 1);
  
  // Find the Sunday before that (start of previous week)
  const lastSunday = new Date(lastSaturday);
  lastSunday.setDate(lastSaturday.getDate() - 6);
  
  return {
    start: lastSunday.toISOString().split('T')[0],
    end: lastSaturday.toISOString().split('T')[0]
  };
}

export default function MapPage({ hideHeader = false }) {
  const withInactive = useInactiveLabels();
  const [visits, setVisits] = useState([]);
  const [zipData, setZipData] = useState([]);
  const [filteredVisits, setFilteredVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [geocoding, setGeocoding] = useState(false);
  
  // Initialize with previous week's dates
  const previousWeek = getPreviousWeekRange();
  
  // Filter states
  const [searchText, setSearchText] = useState('');
  const [startDate, setStartDate] = useState(previousWeek.start);
  const [endDate, setEndDate] = useState(previousWeek.end);
  const [selectedState, setSelectedState] = useState('all');
  const [selectedCity, setSelectedCity] = useState('all');
  const [selectedTaskName, setSelectedTaskName] = useState('all');
  
  // Unique values for filters
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [taskNames, setTaskNames] = useState([]);
  
  // Therapist by City search
  const [citySearchQuery, setCitySearchQuery] = useState('');
  const [therapistResults, setTherapistResults] = useState([]);
  const [searchingTherapists, setSearchingTherapists] = useState(false);
  const [allCities, setAllCities] = useState([]);
  
  // Declinations layer
  const [showDeclinations, setShowDeclinations] = useState(false);
  const [declinations, setDeclinations] = useState([]);
  const [declinationDiscipline, setDeclinationDiscipline] = useState('all');
  const [declinationAgency, setDeclinationAgency] = useState('all');
  const [declinationDisciplines, setDeclinationDisciplines] = useState([]);
  const [declinationAgencies, setDeclinationAgencies] = useState([]);
  
  // Visits layer toggle
  const [showVisits, setShowVisits] = useState(true);
  
  // Applicant Coverage layer
  const [showApplicantCoverage, setShowApplicantCoverage] = useState(false);
  const [applicantCoverages, setApplicantCoverages] = useState([]);

  // Therapist Coverage layer (admin only)
  const [showTherapistCoverage, setShowTherapistCoverage] = useState(false);
  const [therapistCoverages, setTherapistCoverages] = useState([]);  // ZIP rows
  const [therapistCoverageTimeFrame, setTherapistCoverageTimeFrame] = useState('2months');
  const [loadingTherapistCoverage, setLoadingTherapistCoverage] = useState(false);
  const [selectedTherapistArea, setSelectedTherapistArea] = useState(null);
  const [therapistCoverageError, setTherapistCoverageError] = useState(null);
  const [viewByDiscipline, setViewByDiscipline] = useState(false);
  const [selectedDisciplines, setSelectedDisciplines] = useState([]);
  const [availableDisciplines, setAvailableDisciplines] = useState([]);
  const [disciplineColors, setDisciplineColors] = useState({});
  // Density (heatmap) toggle — when OFF, all ZIPs render with uniform opacity
  // so you can show "where we have coverage" without leaking visit-count info.
  const [coverageHeatmap, setCoverageHeatmap] = useState(true);
  const [maxVisitsPerZip, setMaxVisitsPerZip] = useState(0);
  // ZIP polygon cache (lazy-loaded, separate request). Map of zip → list of
  // rings. ZIPs not yet in here render as circles; once their polygon arrives
  // the render upgrades automatically.
  const [zipPolygons, setZipPolygons] = useState({});

  // Defensive client-side filter: even if the backend returns a wider set
  // than what was asked for (older deployment, cached payload, stale state),
  // the map and the legend must respect the discipline chip selection.
  const visibleTherapistCoverages = useMemo(() => {
    if (!selectedDisciplines || selectedDisciplines.length === 0) return therapistCoverages;
    const wanted = new Set(selectedDisciplines.map(d => (d || '').toUpperCase()));
    return therapistCoverages.filter(a => wanted.has((a.discipline || '').toUpperCase()));
  }, [therapistCoverages, selectedDisciplines]);

  // Pre-defined discipline colors for "View by Discipline" mode
  const defaultDisciplineColors = {
    "PT": "#E53935",      // Bright Red
    "PTA": "#FF6F00",     // Bright Orange
    "OT": "#1E88E5",      // Bright Blue
    "OTA": "#00ACC1",     // Cyan
    "ST": "#43A047",      // Green
    "SLP": "#43A047",     // Green (same as ST)
    "SN": "#8E24AA",      // Purple
    "RN": "#8E24AA",      // Purple (same as SN)
    "LVN": "#D81B60",     // Pink
    "HHA": "#FDD835",     // Yellow
    "MSW": "#6D4C41",     // Brown
    "COTA": "#00ACC1",    // Cyan (same as OTA)
    "DEFAULT": "#757575"  // Gray for unknown
  };

  useEffect(() => {
    fetchVisits();
    fetchAllCities();
    fetchDeclinationMetadata();
  }, []);
  
  const fetchAllCities = async () => {
    try {
      const response = await axios.get(`${API}/visits/cities`);
      setAllCities(response.data);
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };
  
  const fetchDeclinationMetadata = async () => {
    try {
      const [discRes, agencyRes] = await Promise.all([
        axios.get(`${API}/declinations/disciplines`),
        axios.get(`${API}/declinations/agencies`)
      ]);
      setDeclinationDisciplines(discRes.data.disciplines || []);
      setDeclinationAgencies(agencyRes.data.agencies || []);
    } catch (error) {
      console.error('Error fetching declination metadata:', error);
    }
  };
  
  const fetchDeclinations = async () => {
    try {
      const params = {};
      if (declinationDiscipline !== 'all') params.discipline = declinationDiscipline;
      if (declinationAgency !== 'all') params.agency = declinationAgency;
      // Don't filter declinations by visit date range - show all declinations
      
      const response = await axios.get(`${API}/declinations/map-data`, { params });
      setDeclinations(response.data);
    } catch (error) {
      console.error('Error fetching declinations:', error);
    }
  };
  
  const fetchApplicantCoverages = async () => {
    try {
      const response = await axios.get(`${API}/ats/coverage/all`);
      setApplicantCoverages(response.data);
    } catch (error) {
      console.error('Error fetching applicant coverages:', error);
    }
  };

  const therapistCovAbortRef = useRef(null);
  // Background polygon-fetcher reference so we can abort / restart if the
  // ZIP set changes (filters, time-frame).
  const polygonFetchAbortRef = useRef(null);

  // Whenever the visible territory ZIPs change, lazily fetch the polygons
  // for any we haven't seen yet — in chunks of 75, with polling for states
  // that are still being downloaded server-side. Fully async; the map keeps
  // working with circles until the polygons arrive.
  useEffect(() => {
    const want = visibleTherapistCoverages
      .map(r => r.zip)
      .filter(z => z && !zipPolygons[z]);
    const uniqueWant = [...new Set(want)];
    if (uniqueWant.length === 0) return;

    if (polygonFetchAbortRef.current) {
      polygonFetchAbortRef.current.abort();
    }
    const controller = new AbortController();
    polygonFetchAbortRef.current = controller;

    let cancelled = false;
    const fetchPolygons = async () => {
      const CHUNK = 75;
      // Track which states are still being downloaded server-side
      const pendingByChunk = [];
      for (let i = 0; i < uniqueWant.length; i += CHUNK) {
        if (cancelled) return;
        const chunk = uniqueWant.slice(i, i + CHUNK);
        try {
          const token = localStorage.getItem('token');
          const resp = await axios.get(`${API}/zip-polygons`, {
            params: { zips: chunk.join(',') },
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
            timeout: 15000,
          });
          if (cancelled) return;
          if (resp.data?.polygons && Object.keys(resp.data.polygons).length > 0) {
            setZipPolygons(prev => ({ ...prev, ...resp.data.polygons }));
          }
          if (resp.data?.pending_states?.length > 0) {
            pendingByChunk.push({ zips: chunk, states: resp.data.pending_states });
          }
        } catch (e) {
          if (axios.isCancel(e) || e.name === 'CanceledError') return;
          // Keep going — one chunk failing shouldn't break the rest
          // eslint-disable-next-line no-console
          console.warn('zip-polygons chunk failed:', e?.message);
        }
      }
      // Poll any chunks whose state was still downloading
      let attempts = 0;
      while (pendingByChunk.length > 0 && attempts < 12 && !cancelled) {
        attempts++;
        await new Promise(r => setTimeout(r, 3000));
        if (cancelled) return;
        for (let i = pendingByChunk.length - 1; i >= 0; i--) {
          const { zips: chunkZips } = pendingByChunk[i];
          try {
            const token = localStorage.getItem('token');
            const resp = await axios.get(`${API}/zip-polygons`, {
              params: { zips: chunkZips.join(',') },
              headers: { Authorization: `Bearer ${token}` },
              signal: controller.signal,
              timeout: 15000,
            });
            if (cancelled) return;
            if (resp.data?.polygons && Object.keys(resp.data.polygons).length > 0) {
              setZipPolygons(prev => ({ ...prev, ...resp.data.polygons }));
            }
            if (!resp.data?.pending_states || resp.data.pending_states.length === 0) {
              pendingByChunk.splice(i, 1);
            }
          } catch (e) {
            if (axios.isCancel(e)) return;
            // Drop this chunk and move on
            pendingByChunk.splice(i, 1);
          }
        }
      }
    };
    fetchPolygons();
    return () => { cancelled = true; controller.abort(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleTherapistCoverages]);

  const fetchTherapistCoverages = async () => {
    // Abort any in-flight request from a previous filter change
    if (therapistCovAbortRef.current) {
      therapistCovAbortRef.current.abort();
    }
    const controller = new AbortController();
    therapistCovAbortRef.current = controller;

    setLoadingTherapistCoverage(true);
    setTherapistCoverageError(null);
    try {
      const token = localStorage.getItem('token');
      const params = { 
        time_frame: therapistCoverageTimeFrame,
        // Auto-activate discipline merge when specific disciplines are selected
        view_by_discipline: viewByDiscipline || selectedDisciplines.length > 0
      };
      
      // Add discipline filter if selected
      if (selectedDisciplines.length > 0) {
        params.disciplines = selectedDisciplines.join(',');
      }
      
      const response = await axios.get(`${API}/therapist-coverage-areas`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
        timeout: 30000,
      });
      
      // Only commit results from the most recent request
      if (therapistCovAbortRef.current === controller) {
        // New ZIP-based shape — the endpoint now returns `zips`, not
        // `coverage_areas`. Each row is { group_id, label, discipline,
        // color, zip, city, lat, lng, visits, therapists }.
        setTherapistCoverages(response.data.zips || []);
        setMaxVisitsPerZip(response.data.max_visits_per_zip || 0);
        setAvailableDisciplines(response.data.disciplines || []);
        if (response.data.discipline_colors) {
          setDisciplineColors(response.data.discipline_colors);
        }
      }
    } catch (error) {
      // Silently swallow cancellations — they happen by design when filters change rapidly
      if (axios.isCancel(error) || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        return;
      }
      console.error('Error fetching therapist coverages:', error);
      // Only surface a toast for the latest in-flight request
      if (therapistCovAbortRef.current === controller) {
        const msg = error.response?.data?.detail || error.message || 'Failed to load therapist coverage data';
        setTherapistCoverageError(msg);
        toast.error(`Failed to load therapist coverage data — ${msg}`);
      }
    } finally {
      if (therapistCovAbortRef.current === controller) {
        setLoadingTherapistCoverage(false);
      }
    }
  };
  
  // Fetch declinations when layer is toggled on or filters change
  useEffect(() => {
    if (showDeclinations) {
      fetchDeclinations();
    }
  }, [showDeclinations, declinationDiscipline, declinationAgency]);
  
  // Fetch coverage when layer is toggled on
  useEffect(() => {
    if (showApplicantCoverage) {
      fetchApplicantCoverages();
    }
  }, [showApplicantCoverage]);

  // Fetch therapist coverage when layer is toggled on or filters change.
  // Debounced 250ms to coalesce rapid filter changes into one request.
  useEffect(() => {
    if (!showTherapistCoverage) return;
    const handle = setTimeout(() => {
      fetchTherapistCoverages();
    }, 250);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTherapistCoverage, therapistCoverageTimeFrame, viewByDiscipline, selectedDisciplines]);
  
  const getDeclinationIcon = (discipline) => {
    const upper = (discipline || '').toUpperCase();
    return icons[`declination_${upper}`] || icons.declination_default;
  };
  
  const searchTherapistsByCity = async () => {
    if (!citySearchQuery.trim()) {
      toast.error('Please enter a city name');
      return;
    }
    
    setSearchingTherapists(true);
    try {
      const response = await axios.get(`${API}/therapists/by-city`, {
        params: { city: citySearchQuery }
      });
      setTherapistResults(response.data);
      if (response.data.length === 0) {
        toast.info('No therapists found with visits in this city');
      }
    } catch (error) {
      console.error('Error searching therapists:', error);
      toast.error('Search failed');
    } finally {
      setSearchingTherapists(false);
    }
  };

  const fetchVisits = async () => {
    setLoading(true);
    try {
      // Fetch both regular visits (for filtering) and zip-aggregated data (for map)
      const [visitsRes, zipRes] = await Promise.all([
        axios.get(`${API}/visits/with-coordinates`),
        axios.get(`${API}/visits/by-zipcode`)
      ]);
      
      const data = visitsRes.data;
      setVisits(data);
      setZipData(zipRes.data);
      
      // Extract unique values for filters
      const uniqueStates = [...new Set(data.map(v => v.state).filter(Boolean))].sort();
      const uniqueCities = [...new Set(data.map(v => v.city).filter(Boolean))].sort();
      const uniqueTaskNames = [...new Set(data.map(v => v.task_name).filter(Boolean))].sort();
      
      setStates(uniqueStates);
      setCities(uniqueCities);
      setTaskNames(uniqueTaskNames);
      
    } catch (error) {
      console.error('Error fetching visits:', error);
      toast.error('Failed to load visits');
    } finally {
      setLoading(false);
    }
  };

  const handleGeocode = async () => {
    setGeocoding(true);
    try {
      const response = await axios.post(`${API}/visits/geocode-zipcodes`);
      toast.success(`Geocoded ${response.data.geocoded} zip codes. ${response.data.remaining} remaining.`);
      fetchVisits();
    } catch (error) {
      console.error('Geocoding error:', error);
      toast.error('Geocoding failed');
    } finally {
      setGeocoding(false);
    }
  };

  // Apply filters
  useEffect(() => {
    let result = [...visits];
    
    // Text search
    if (searchText) {
      const search = searchText.toLowerCase();
      result = result.filter(v => 
        (v.first_name && v.first_name.toLowerCase().includes(search)) ||
        (v.last_name && v.last_name.toLowerCase().includes(search)) ||
        (v.address && v.address.toLowerCase().includes(search)) ||
        (v.city && v.city.toLowerCase().includes(search)) ||
        (v.zip_code && v.zip_code.includes(search)) ||
        (v.agency && v.agency.toLowerCase().includes(search))
      );
    }
    
    // Date range filter - handle both YYYY-MM-DD and MM/DD/YYYY formats
    if (startDate || endDate) {
      result = result.filter(v => {
        if (!v.date) return false;
        
        // Convert visit date to comparable format
        let visitDate = v.date;
        if (v.date.includes('/')) {
          // Convert MM/DD/YYYY to YYYY-MM-DD
          const parts = v.date.split('/');
          if (parts.length === 3) {
            visitDate = `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
          }
        }
        
        if (startDate && visitDate < startDate) return false;
        if (endDate && visitDate > endDate) return false;
        return true;
      });
    }
    
    // State filter
    if (selectedState && selectedState !== 'all') {
      result = result.filter(v => v.state === selectedState);
    }
    
    // City filter
    if (selectedCity && selectedCity !== 'all') {
      result = result.filter(v => v.city === selectedCity);
    }
    
    // Task name filter
    if (selectedTaskName && selectedTaskName !== 'all') {
      result = result.filter(v => v.task_name === selectedTaskName);
    }
    
    setFilteredVisits(result);
  }, [visits, searchText, startDate, endDate, selectedState, selectedCity, selectedTaskName]);

  // Get positions for markers - use zip data with filtering applied
  const positions = useMemo(() => {
    // Filter zip data based on the filtered visits
    const filteredZipCodes = new Set();
    
    filteredVisits.forEach(v => {
      const zip = (v.zip_code || '').substring(0, 5);
      if (zip) filteredZipCodes.add(zip);
    });
    
    // Count visits per zip from filtered visits
    const zipCounts = {};
    filteredVisits.forEach(v => {
      const zip = (v.zip_code || '').substring(0, 5);
      if (zip) {
        zipCounts[zip] = (zipCounts[zip] || 0) + 1;
      }
    });
    
    return zipData
      .filter(z => z.latitude && z.longitude && filteredZipCodes.has(z.zip_code))
      .map(z => ({
        lat: z.latitude,
        lng: z.longitude,
        zip_code: z.zip_code,
        city: z.city,
        state: z.state,
        count: zipCounts[z.zip_code] || z.count,
        visits: z.visits.filter(visit => {
          // Apply same filters to visits within zip
          if (!visit.date) return false;
          let visitDate = visit.date;
          if (visit.date.includes('/')) {
            const parts = visit.date.split('/');
            if (parts.length === 3) {
              visitDate = `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
            }
          }
          if (startDate && visitDate < startDate) return false;
          if (endDate && visitDate > endDate) return false;
          return true;
        })
      }));
  }, [zipData, filteredVisits, startDate, endDate]);

  const clearFilters = () => {
    setSearchText('');
    setStartDate(previousWeek.start);
    setEndDate(previousWeek.end);
    setSelectedState('all');
    setSelectedCity('all');
    setSelectedTaskName('all');
  };

  const getMarkerIcon = (status) => {
    const statusLower = (status || '').toLowerCase();
    if (statusLower.includes('complete')) return icons.completed;
    if (statusLower.includes('pending') || statusLower.includes('scheduled')) return icons.pending;
    if (statusLower.includes('cancel') || statusLower.includes('missed')) return icons.cancelled;
    return icons.default;
  };

  // Update cities when state changes
  useEffect(() => {
    if (selectedState && selectedState !== 'all') {
      const citiesInState = [...new Set(
        visits
          .filter(v => v.state === selectedState)
          .map(v => v.city)
          .filter(Boolean)
      )].sort();
      setCities(citiesInState);
    } else {
      const allCitiesList = [...new Set(visits.map(v => v.city).filter(Boolean))].sort();
      setCities(allCitiesList);
    }
    setSelectedCity('all');
  }, [selectedState, visits]);

  return (
    <div className="space-y-6">
      {!hideHeader && (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold text-foreground">Reports & Map</h1>
            <p className="text-muted-foreground mt-1">
              Location-based reporting: Visits, Declinations, and Coverage Areas
            </p>
          </div>
          <Button
            onClick={handleGeocode}
            disabled={geocoding}
            variant="outline"
            className="gap-2"
          >
            <RefreshCw size={16} className={geocoding ? 'animate-spin' : ''} />
            {geocoding ? 'Geocoding...' : 'Update Zip Coordinates'}
          </Button>
        </div>
      )}
      
      {hideHeader && (
        <div className="flex justify-end">
          <Button
            onClick={handleGeocode}
            disabled={geocoding}
            variant="outline"
            className="gap-2"
          >
            <RefreshCw size={16} className={geocoding ? 'animate-spin' : ''} />
            {geocoding ? 'Geocoding...' : 'Update Zip Coordinates'}
          </Button>
        </div>
      )}
      
      {/* Map Layers Control */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-4">
          <Layers size={18} className="text-primary" />
          <h3 className="font-medium">Map Layers</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2">
              <MapPin size={16} className="text-blue-500" />
              <Label htmlFor="show-visits" className="text-sm font-medium cursor-pointer">
                Visits
              </Label>
            </div>
            <div className="flex items-center gap-2">
              {showVisits && <Badge variant="secondary">{positions.length}</Badge>}
              <Switch
                id="show-visits"
                checked={showVisits}
                onCheckedChange={setShowVisits}
              />
            </div>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertTriangle size={16} className="text-orange-500" />
              <Label htmlFor="show-declinations" className="text-sm font-medium cursor-pointer">
                Declinations
              </Label>
            </div>
            <div className="flex items-center gap-2">
              {showDeclinations && <Badge variant="outline">{declinations.length}</Badge>}
              <Switch
                id="show-declinations"
                checked={showDeclinations}
                onCheckedChange={setShowDeclinations}
              />
            </div>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2">
              <UserSearch size={16} className="text-purple-500" />
              <Label htmlFor="show-coverage" className="text-sm font-medium cursor-pointer">
                Applicant Coverage
              </Label>
            </div>
            <div className="flex items-center gap-2">
              {showApplicantCoverage && <Badge variant="outline">{applicantCoverages.length}</Badge>}
              <Switch
                id="show-coverage"
                checked={showApplicantCoverage}
                onCheckedChange={setShowApplicantCoverage}
              />
            </div>
          </div>
          
          {/* Therapist Coverage Layer */}
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2">
              <Stethoscope size={16} className="text-green-500" />
              <Label htmlFor="show-therapist-coverage" className="text-sm font-medium cursor-pointer">
                Therapist Territories
              </Label>
            </div>
            <div className="flex items-center gap-2">
              {showTherapistCoverage && <Badge variant="outline">{visibleTherapistCoverages.length}</Badge>}
              {loadingTherapistCoverage && <div className="w-4 h-4 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />}
              <Switch
                id="show-therapist-coverage"
                data-testid="show-therapist-coverage"
                checked={showTherapistCoverage}
                onCheckedChange={setShowTherapistCoverage}
              />
            </div>
          </div>
        </div>
        
        {/* Therapist Coverage Settings (shown when layer is active) */}
        {showTherapistCoverage && (
          <div className="mt-4 pt-4 border-t">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium">Therapist Territory Settings</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  {visibleTherapistCoverages.length} ZIP{visibleTherapistCoverages.length === 1 ? '' : 's'} with coverage
                  {selectedDisciplines.length > 0 && therapistCoverages.length !== visibleTherapistCoverages.length && (
                    <span className="ml-1">(filtered from {therapistCoverages.length})</span>
                  )}
                </span>
              </div>
            </div>
            
            {/* View Mode Toggle */}
            <div className="mb-4 p-3 bg-muted/30 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">View by Discipline</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Merge all therapists into one color per discipline (otherwise each therapist gets their own color)
                  </p>
                </div>
                <Switch
                  checked={viewByDiscipline}
                  onCheckedChange={setViewByDiscipline}
                  data-testid="toggle-view-by-discipline"
                />
              </div>
              <div className="flex items-center justify-between pt-3 border-t">
                <div>
                  <Label className="text-sm font-medium">Density Heatmap</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    When ON, ZIPs with more visits appear darker. Turn OFF to show coverage only — every covered ZIP filled uniformly regardless of volume.
                  </p>
                </div>
                <Switch
                  checked={coverageHeatmap}
                  onCheckedChange={setCoverageHeatmap}
                  data-testid="toggle-coverage-heatmap"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs">Time Frame</Label>
                <Select value={therapistCoverageTimeFrame} onValueChange={setTherapistCoverageTimeFrame}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select time frame" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1month">Last 1 Month</SelectItem>
                    <SelectItem value="2months">Last 2 Months</SelectItem>
                    <SelectItem value="3months">Last 3 Months</SelectItem>
                    <SelectItem value="6months">Last 6 Months</SelectItem>
                    <SelectItem value="1year">Last Year</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Discipline Filter */}
              <div>
                <Label className="text-xs">Filter by Discipline</Label>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {['PT', 'PTA', 'OT', 'OTA', 'ST', 'SN', 'LVN', 'HHA', 'MSW'].map((disc) => {
                    const isSelected = selectedDisciplines.includes(disc);
                    const discColor = defaultDisciplineColors[disc] || defaultDisciplineColors.DEFAULT;
                    return (
                      <button
                        key={disc}
                        onClick={() => {
                          if (isSelected) {
                            setSelectedDisciplines(selectedDisciplines.filter(d => d !== disc));
                          } else {
                            setSelectedDisciplines([...selectedDisciplines, disc]);
                          }
                        }}
                        className={`px-2 py-1 text-xs rounded-md border transition-all ${
                          isSelected 
                            ? 'text-white font-medium' 
                            : 'bg-background hover:bg-muted'
                        }`}
                        style={isSelected ? { backgroundColor: discColor, borderColor: discColor } : {}}
                      >
                        {disc}
                      </button>
                    );
                  })}
                  {selectedDisciplines.length > 0 && (
                    <button
                      onClick={() => setSelectedDisciplines([])}
                      className="px-2 py-1 text-xs rounded-md border text-muted-foreground hover:bg-muted"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
              
              <div className="flex items-end">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={fetchTherapistCoverages}
                  disabled={loadingTherapistCoverage}
                  className="w-full"
                >
                  <RefreshCw size={14} className={loadingTherapistCoverage ? 'animate-spin mr-1' : 'mr-1'} />
                  Refresh Coverage
                </Button>
              </div>
            </div>

            {therapistCoverageError && (
              <div className="mt-3 px-3 py-2 rounded-md border border-red-200 bg-red-50 text-xs text-red-800 flex items-start gap-2" data-testid="therapist-coverage-error">
                <span className="flex-1">
                  Couldn&apos;t load therapist data: {therapistCoverageError}
                </span>
                <button
                  type="button"
                  onClick={fetchTherapistCoverages}
                  className="font-semibold underline shrink-0"
                  data-testid="therapist-coverage-retry"
                >
                  Retry
                </button>
              </div>
            )}
            
            {/* Discipline Color Legend (when View by Discipline is enabled) */}
            {viewByDiscipline && (
              <div className="mt-4 pt-4 border-t">
                <div className="text-xs font-medium mb-2">Discipline Colors</div>
                <div className="flex flex-wrap gap-3">
                  {Object.entries(defaultDisciplineColors)
                    .filter(([key]) => key !== 'DEFAULT' && key !== 'SLP' && key !== 'RN' && key !== 'COTA')
                    .map(([disc, color]) => {
                      // Honor explicit chip selection — if the user picked specific
                      // disciplines, the legend must only show those.
                      if (selectedDisciplines.length > 0 && !selectedDisciplines.includes(disc)) return null;
                      const count = visibleTherapistCoverages.filter(a =>
                        a.discipline?.toUpperCase() === disc
                      ).length;
                      if (count === 0 && selectedDisciplines.length === 0) return null;
                      return (
                        <div key={disc} className="flex items-center gap-1.5 text-xs">
                          <div 
                            className="w-4 h-4 rounded-sm" 
                            style={{ backgroundColor: color }}
                          />
                          <span className="font-medium">{disc}</span>
                          {count > 0 && <span className="text-muted-foreground">({count})</span>}
                        </div>
                      );
                    }).filter(Boolean)}
                </div>
              </div>
            )}
            
            {/* Therapist Legend (when View by Discipline is disabled) */}
            {!viewByDiscipline && visibleTherapistCoverages.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <div className="text-xs font-medium mb-2">Territory Legend (click on map to see details)</div>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2 max-h-48 overflow-y-auto">
                  {visibleTherapistCoverages.map((area) => (
                    <div 
                      key={area.profile_id}
                      className="flex items-center gap-2 p-1.5 rounded text-xs hover:bg-muted cursor-pointer"
                      onClick={() => setSelectedTherapistArea(area)}
                    >
                      <div 
                        className="w-3 h-3 rounded-sm flex-shrink-0" 
                        style={{ backgroundColor: area.color }}
                      />
                      <span className="truncate">{withInactive(area.therapist_name)}</span>
                      <span className="text-muted-foreground ml-auto">({area.total_visits})</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Declination Filters (shown when layer is active) */}
        {showDeclinations && (
          <div className="mt-4 pt-4 border-t">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium">Declination Filters</span>
              {declinations.length > 0 && (
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => {
                    // Trigger map to zoom to declinations
                    const event = new CustomEvent('zoomToDeclinations');
                    window.dispatchEvent(event);
                  }}
                >
                  <MapPin size={14} className="mr-1" />
                  Zoom to Declinations
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Filter by Discipline</Label>
                <Select value={declinationDiscipline} onValueChange={setDeclinationDiscipline}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="All Disciplines" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Disciplines</SelectItem>
                    {declinationDisciplines.map(d => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Filter by Agency</Label>
                <Select value={declinationAgency} onValueChange={setDeclinationAgency}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="All Agencies" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Agencies</SelectItem>
                    {declinationAgencies.map(a => (
                      <SelectItem key={a} value={a}>{a}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Therapist by City Search */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-4">
          <UserSearch size={18} className="text-primary" />
          <h3 className="font-medium">Find Therapists by City</h3>
        </div>
        
        <div className="flex gap-3 mb-4">
          <div className="flex-1">
            <Input
              placeholder="Enter city name (e.g., Houston, Dallas)..."
              value={citySearchQuery}
              onChange={(e) => setCitySearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && searchTherapistsByCity()}
              list="city-suggestions"
            />
            <datalist id="city-suggestions">
              {allCities.slice(0, 50).map(city => (
                <option key={city} value={city} />
              ))}
            </datalist>
          </div>
          <Button 
            onClick={searchTherapistsByCity}
            disabled={searchingTherapists}
            className="gap-2"
          >
            <Search size={16} />
            {searchingTherapists ? 'Searching...' : 'Search'}
          </Button>
        </div>
        
        {therapistResults.length > 0 && (
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left p-3 font-medium">Therapist</th>
                  <th className="text-left p-3 font-medium">Discipline</th>
                  <th className="text-left p-3 font-medium">State</th>
                  <th className="text-center p-3 font-medium">Visits in City</th>
                  <th className="text-center p-3 font-medium">Profile</th>
                </tr>
              </thead>
              <tbody>
                {therapistResults.map((therapist, index) => (
                  <tr key={therapist.id || index} className="border-t hover:bg-muted/50">
                    <td className="p-3 font-medium">
                      <Link 
                        to={`/personnel?search=${encodeURIComponent(`${therapist.first_name} ${therapist.last_name}`)}`}
                        className="text-primary hover:underline"
                      >
                        {withInactive(`${therapist.first_name} ${therapist.last_name}`)}
                      </Link>
                    </td>
                    <td className="p-3">
                      <span className="inline-flex items-center gap-1.5">
                        <Stethoscope size={14} className="text-primary" />
                        {therapist.discipline || 'Not specified'}
                      </span>
                    </td>
                    <td className="p-3 text-muted-foreground">
                      {therapist.state || '-'}
                    </td>
                    <td className="p-3 text-center">
                      <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 bg-primary/10 text-primary rounded-full text-xs font-medium">
                        {therapist.visits_in_city}
                      </span>
                    </td>
                    <td className="p-3 text-center">
                      <Link to={`/personnel?search=${encodeURIComponent(`${therapist.first_name} ${therapist.last_name}`)}`}>
                        <Button variant="ghost" size="sm">
                          View
                        </Button>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="p-3 bg-muted/50 text-sm text-muted-foreground border-t">
              Found {therapistResults.length} therapist{therapistResults.length !== 1 ? 's' : ''} with visits in &quot;{citySearchQuery}&quot;
            </div>
          </div>
        )}
      </Card>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-4">
          <Filter size={18} className="text-muted-foreground" />
          <h3 className="font-medium">Filter Visits on Map</h3>
          {(searchText || startDate !== previousWeek.start || endDate !== previousWeek.end || selectedState !== 'all' || selectedCity !== 'all' || selectedTaskName !== 'all') && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="ml-auto gap-1">
              <X size={14} />
              Reset to Previous Week
            </Button>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          {/* Search */}
          <div>
            <Label className="text-xs">Search</Label>
            <Input
              placeholder="Name, address, city..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="mt-1"
            />
          </div>
          
          {/* Date Range */}
          <div>
            <Label className="text-xs">From Date</Label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="mt-1 max-w-[160px]"
            />
          </div>
          <div>
            <Label className="text-xs">To Date</Label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="mt-1 max-w-[160px]"
            />
          </div>
          
          {/* State */}
          <div>
            <Label className="text-xs">State</Label>
            <Select value={selectedState} onValueChange={setSelectedState}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="All States" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All States</SelectItem>
                {states.map(state => (
                  <SelectItem key={state} value={state}>{state}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* City */}
          <div>
            <Label className="text-xs">City</Label>
            <Select value={selectedCity} onValueChange={setSelectedCity}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="All Cities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cities</SelectItem>
                {cities.map(city => (
                  <SelectItem key={city} value={city}>{city}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Task Name */}
          <div>
            <Label className="text-xs">Task Type</Label>
            <Select value={selectedTaskName} onValueChange={setSelectedTaskName}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="All Tasks" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tasks</SelectItem>
                {taskNames.map(task => (
                  <SelectItem key={task} value={task}>{task}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <p className="text-sm text-muted-foreground mt-3">
          Showing {filteredVisits.length} visits across {positions.length} zip codes on map
        </p>
      </Card>

      {/* Map */}
      <Card className="p-0 overflow-hidden relative" style={{ zIndex: 0 }}>
        {loading ? (
          <div className="h-[600px] flex items-center justify-center bg-muted">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
              <p className="text-muted-foreground">Loading map data...</p>
            </div>
          </div>
        ) : (
          <div style={{ width: '100%', aspectRatio: '1 / 1' }}>
          <MapContainer
            center={[32.7767, -96.7970]} // Dallas, TX as default center
            zoom={8}
            style={{ height: '100%', width: '100%', zIndex: 0 }}
            scrollWheelZoom={true}
          >
            {/* Base map WITHOUT labels — the colored territories draw on top of this.
                Esri Light Gray Canvas (no API key required). */}
            <TileLayer
              attribution='Tiles &copy; Esri &mdash; Esri, HERE, Garmin, &copy; OpenStreetMap contributors'
              url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}"
              maxNativeZoom={16}
              maxZoom={19}
            />

            {/* Labels-only layer rendered in a high z-index pane so town / city
                names stay readable ON TOP of the colored territory overlays. */}
            <Pane name="map-labels" style={{ zIndex: 620, pointerEvents: 'none' }}>
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}"
                maxNativeZoom={16}
                maxZoom={19}
                pane="map-labels"
              />
            </Pane>
            
            {positions.length > 0 && showVisits && <FitBounds positions={positions} />}
            {showDeclinations && <ZoomToDeclinations declinations={declinations} />}
            
            {/* Visits Layer */}
            {showVisits && (
            <MarkerClusterGroup
              chunkedLoading
              maxClusterRadius={20}
              spiderfyOnMaxZoom={true}
              showCoverageOnHover={false}
              disableClusteringAtZoom={5}
            >
              {positions.map((pos, index) => {
                // Aggregate therapists and agencies for this ZIP
                const therapistMap = {};
                const agencyMap = {};
                
                (pos.visits || []).forEach(v => {
                  // Therapists
                  const therapistName = `${v.first_name || ''} ${v.last_name || ''}`.trim();
                  const therapistId = v.therapist_id || v.profile_id || v.id;
                  if (therapistName) {
                    const key = therapistId || therapistName;
                    if (!therapistMap[key]) {
                      therapistMap[key] = { name: therapistName, id: therapistId, count: 0 };
                    }
                    therapistMap[key].count++;
                  }
                  
                  // Agencies
                  const agencyName = v.agency_name || v.agency;
                  const agencyId = v.agency_id;
                  if (agencyName) {
                    const key = agencyId || agencyName;
                    if (!agencyMap[key]) {
                      agencyMap[key] = { name: agencyName, id: agencyId, count: 0 };
                    }
                    agencyMap[key].count++;
                  }
                });
                
                const therapists = Object.values(therapistMap).sort((a, b) => b.count - a.count);
                const agencies = Object.values(agencyMap).sort((a, b) => b.count - a.count);
                
                return (
                  <Marker
                    key={pos.zip_code || index}
                    position={[pos.lat, pos.lng]}
                    icon={icons.default}
                  >
                    <Popup>
                      <div className="min-w-[280px] max-w-[320px]">
                        <h4 className="font-semibold text-sm mb-1">
                          {pos.city}, {pos.state} {pos.zip_code}
                        </h4>
                        <p className="text-lg font-bold text-primary mb-3">
                          {pos.count} visits
                        </p>
                        
                        {/* Therapists */}
                        {therapists.length > 0 && (
                          <div className="mb-3">
                            <p className="text-xs font-semibold text-muted-foreground mb-1">
                              THERAPISTS ({therapists.length})
                            </p>
                            <div className="max-h-24 overflow-y-auto text-xs space-y-1">
                              {therapists.slice(0, 5).map((t, i) => (
                                <div key={i} className="flex justify-between items-center">
                                  <Link 
                                    to={`/personnel?search=${encodeURIComponent(t.name)}`}
                                    className="text-primary hover:underline truncate mr-2"
                                  >
                                    {withInactive(t.name)}
                                  </Link>
                                  <span className="text-muted-foreground whitespace-nowrap">
                                    {t.count} visits
                                  </span>
                                </div>
                              ))}
                              {therapists.length > 5 && (
                                <p className="text-muted-foreground italic">
                                  +{therapists.length - 5} more therapists
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Agencies */}
                        {agencies.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-muted-foreground mb-1">
                              AGENCIES ({agencies.length})
                            </p>
                            <div className="max-h-24 overflow-y-auto text-xs space-y-1">
                              {agencies.slice(0, 5).map((a, i) => (
                                <div key={i} className="flex justify-between items-center">
                                  {a.id ? (
                                    <Link 
                                      to={`/agencies/${a.id}`}
                                      className="text-primary hover:underline truncate mr-2"
                                    >
                                      {a.name}
                                    </Link>
                                  ) : (
                                    <span className="truncate mr-2">{a.name}</span>
                                  )}
                                  <span className="text-muted-foreground whitespace-nowrap">
                                    {a.count} visits
                                  </span>
                                </div>
                              ))}
                              {agencies.length > 5 && (
                                <p className="text-muted-foreground italic">
                                  +{agencies.length - 5} more agencies
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MarkerClusterGroup>
            )}
            
            {/* Applicant Coverage Areas */}
            {showApplicantCoverage && applicantCoverages.map((coverage, index) => {
              const color = coverageColors[index % coverageColors.length];
              
              if (coverage.coverage_type === 'radius' && coverage.center_lat && coverage.center_lng) {
                return (
                  <Circle
                    key={coverage.applicant_id}
                    center={[coverage.center_lat, coverage.center_lng]}
                    radius={(coverage.radius_miles || 25) * 1609.34}
                    pathOptions={{ color, fillColor: color, fillOpacity: 0.15, weight: 2 }}
                  >
                    <Popup>
                      <div className="text-sm">
                        <Link 
                          to={`/ats/applicants/${coverage.applicant_id}`}
                          className="font-medium text-primary hover:underline"
                        >
                          {coverage.applicant_name}
                        </Link>
                        <p className="text-muted-foreground">{coverage.desired_position}</p>
                        <p className="text-xs mt-1">{coverage.radius_miles} mile radius</p>
                        <Badge variant="outline" className="mt-1">{coverage.status}</Badge>
                      </div>
                    </Popup>
                  </Circle>
                );
              }
              
              if (coverage.polygon_coordinates?.length >= 3) {
                return (
                  <Polygon
                    key={coverage.applicant_id}
                    positions={coverage.polygon_coordinates.map(p => [p.lat, p.lng])}
                    pathOptions={{ color, fillColor: color, fillOpacity: 0.15, weight: 2 }}
                  >
                    <Popup>
                      <div className="text-sm">
                        <Link 
                          to={`/ats/applicants/${coverage.applicant_id}`}
                          className="font-medium text-primary hover:underline"
                        >
                          {coverage.applicant_name}
                        </Link>
                        <p className="text-muted-foreground">{coverage.desired_position}</p>
                        <p className="text-xs mt-1 capitalize">{coverage.coverage_type}</p>
                        <Badge variant="outline" className="mt-1">{coverage.status}</Badge>
                      </div>
                    </Popup>
                  </Polygon>
                );
              }
              
              return null;
            })}
            
            {/* Declination Markers */}
            {showDeclinations && (
              <MarkerClusterGroup
                chunkedLoading
                maxClusterRadius={30}
              >
                {declinations.map((dec) => (
                  <Marker
                    key={dec.id}
                    position={[dec.latitude, dec.longitude]}
                    icon={getDeclinationIcon(dec.discipline)}
                  >
                    <Popup>
                      <div className="text-sm min-w-[200px]">
                        <div className="flex items-center gap-2 mb-2">
                          <AlertTriangle size={16} className="text-orange-500" />
                          <span className="font-medium">Declined Visit</span>
                        </div>
                        <p className="font-medium">{dec.address}</p>
                        <p className="text-muted-foreground">{dec.city}, {dec.state} {dec.zip_code}</p>
                        <div className="flex gap-2 mt-2">
                          <Badge>{dec.discipline}</Badge>
                          <Badge variant="outline">{dec.date}</Badge>
                        </div>
                        {dec.agency && (
                          <p className="text-sm mt-2">
                            <span className="text-muted-foreground">Agency: </span>
                            <Link 
                              to={`/agencies?search=${encodeURIComponent(dec.agency)}`}
                              className="text-primary hover:underline"
                            >
                              {dec.agency}
                            </Link>
                          </p>
                        )}
                        {dec.reason && (
                          <p className="text-xs text-muted-foreground italic mt-1">
                            Reason: {dec.reason}
                          </p>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MarkerClusterGroup>
            )}
            
            {/* Therapist Coverage — actual ZIP boundary polygons (with
                circle fallback for any ZIP whose boundary the backend
                couldn't resolve). A polygon's fill opacity scales with
                visit count when the Density toggle is ON, or stays uniform
                when OFF (so admins can show "where we have coverage"
                without leaking visit volume). */}
            {showTherapistCoverage && visibleTherapistCoverages.map((row, idx) => {
              if (!row.lat || !row.lng) return null;
              const denom = Math.max(maxVisitsPerZip || 1, 1);
              const ratio = Math.min(1, Math.max(0.1, (row.visits || 0) / denom));
              const fillOpacity = coverageHeatmap ? 0.20 + (ratio * 0.55) : 0.40;
              const isDisciplineView = String(row.group_id || '').startsWith('disc:');

              const popup = (
                <Popup>
                  <div className="text-sm min-w-[220px]">
                    <div className="flex items-center gap-2 mb-2">
                      <Stethoscope size={16} className="text-green-500" />
                      <span className="font-semibold">{row.label}</span>
                    </div>
                    <div className="space-y-1.5">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">ZIP:</span>
                        <span className="font-medium">{row.zip}{row.city ? ` · ${row.city}` : ''}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Discipline:</span>
                        <Badge variant="secondary">{row.discipline || '—'}</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Visits in window:</span>
                        <span className="font-medium">{row.visits}</span>
                      </div>
                      {isDisciplineView && row.therapists?.length > 0 && (
                        <div className="pt-2 border-t mt-2">
                          <p className="text-xs font-medium mb-1">Therapists ({row.therapists.length}):</p>
                          <div className="text-xs text-muted-foreground max-h-24 overflow-y-auto">
                            {row.therapists.slice(0, 8).map((name, i) => (
                              <div key={i}>
                                <Link
                                  to={`/personnel?search=${encodeURIComponent(name)}`}
                                  className="text-primary hover:underline"
                                >
                                  {name}
                                </Link>
                              </div>
                            ))}
                            {row.therapists.length > 8 && (
                              <p className="italic mt-1">+{row.therapists.length - 8} more</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    {!isDisciplineView && row.therapists?.[0] && (
                      <Link
                        to={`/personnel?search=${encodeURIComponent(row.therapists[0])}`}
                        className="mt-3 block text-center text-xs text-primary hover:underline"
                      >
                        View Personnel Record →
                      </Link>
                    )}
                  </div>
                </Popup>
              );

              const rings = (row.polygon && row.polygon.length > 0) ? row.polygon : zipPolygons[row.zip];
              if (rings && rings.length > 0) {
                // Render a real ZIP shape. react-leaflet accepts either a
                // single ring (Array of [lat,lng]) or an array of rings
                // (multi-polygon). Convert {lat,lng} objects to tuples.
                const positions = rings.map(ring =>
                  ring.map(p => [p.lat, p.lng])
                );
                return (
                  <Polygon
                    key={`poly|${row.group_id}|${row.zip}|${idx}`}
                    positions={positions.length === 1 ? positions[0] : positions}
                    pathOptions={{
                      color: row.color,
                      fillColor: row.color,
                      fillOpacity,
                      weight: 1.5,
                      opacity: 0.85,
                    }}
                    eventHandlers={{ click: () => setSelectedTherapistArea(row) }}
                  >
                    {popup}
                  </Polygon>
                );
              }

              // Fallback when no boundary is available — small circle so the
              // ZIP isn't invisible.
              return (
                <Circle
                  key={`circ|${row.group_id}|${row.zip}|${idx}`}
                  center={[row.lat, row.lng]}
                  radius={5000}
                  pathOptions={{
                    color: row.color,
                    fillColor: row.color,
                    fillOpacity,
                    weight: 1.5,
                    opacity: 0.85,
                  }}
                  eventHandlers={{ click: () => setSelectedTherapistArea(row) }}
                >
                  {popup}
                </Circle>
              );
            })}
          </MapContainer>
          </div>
        )}
      </Card>

      {/* Legend */}
      <Card className="p-4">
        <h4 className="font-medium text-sm mb-3">Map Legend</h4>
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-blue-500"></div>
            <span>Visits</span>
          </div>
          {showDeclinations && (
            <>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-red-500"></div>
                <span>Declined PT</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-blue-600"></div>
                <span>Declined OT</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-green-500"></div>
                <span>Declined ST</span>
              </div>
            </>
          )}
          {showApplicantCoverage && (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-purple-500 opacity-50"></div>
              <span>Applicant Coverage</span>
            </div>
          )}
          {showTherapistCoverage && (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-green-500 opacity-50"></div>
              <span>Therapist Territories</span>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
